import type { TypedUseQuerySubscriptionOptions } from '@reduxjs/toolkit/query/react';
import StructureAPI from '../../../../redux/structure/StructureAPI';
import { CommanderAPI } from '../CommanderAPI';
import _ from 'lodash';
import orComparator from '../../../../shared/util/lodash/merge/orComparator';
import { useAppSelector } from '../../../../redux/store.model';
import { useMemo } from 'react';
import { getCommanderIdsFlexeserveDevicesSelector } from '../../../../redux/structure/selectors/ComposableDevice.selector';
import { isNotNullOrUndefined } from '../../../../shared/util/collection/isNotNullOrUndefined';
import { useParams } from 'react-router-dom';
import { M_SECONDS_POOLING_INTERVAL_DATA } from '../../../store/Store.constants';

type Options = TypedUseQuerySubscriptionOptions;

function useDeviceStatusFromGroup(groupId?: string, options: Options = {}) {
  const { marketId } = useParams<{ marketId: string }>();
  const { data: dataStructure, ...restStructure } =
    StructureAPI.useGetStructureV2Query(marketId, options);

  const params = useMemo(
    () => ({ groupId: groupId ?? '', workspaceId: marketId }),
    [groupId, marketId]
  );
  const commandersIds = dataStructure?.commanderIds;
  const commanderIdsDevices = useAppSelector((state) =>
    getCommanderIdsFlexeserveDevicesSelector(state, params)
  );

  const skip = !commandersIds?.length || options.skip;
  const { data: devicesStatus, ...restDeviceStatus } =
    CommanderAPI.useGetDevicesStatusQuery(commandersIds ?? [], {
      pollingInterval: M_SECONDS_POOLING_INTERVAL_DATA,
      ...options,
      skip,
    });

  const fallingCommandersIds = useMemo(
    () =>
      devicesStatus &&
      commanderIdsDevices &&
      _.uniq(commanderIdsDevices)
        .map((encodedCommanderId) =>
          devicesStatus.entities[encodedCommanderId]?.mqttConnected === false
            ? encodedCommanderId
            : undefined
        )
        .filter(isNotNullOrUndefined),

    [devicesStatus, commanderIdsDevices]
  );

  return {
    ..._.mergeWith({}, restStructure, restDeviceStatus, orComparator),
    data: devicesStatus,
    restDeviceStatus: restDeviceStatus,
    numberFailingMqttConnection: fallingCommandersIds?.length,
    commanderIdsDevices: _.uniq(commanderIdsDevices),
  };
}

export default useDeviceStatusFromGroup;
